import React, { useState, useEffect } from 'react';
import { 
    FaFileAlt, FaDownload, FaCalendarAlt, FaSearch, FaUser, FaChartBar,
    FaGraduationCap, FaCertificate, FaClipboardList, FaSpinner, FaTimes,
    FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaStar, FaComments,
    FaEye, FaExpand, FaCompress, FaFileExcel, FaFilePdf, FaPrint, FaSync
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig.js';
import LoadingSpinner from './LoadingSpinner';

const NonAdminReportCenter = ({ userType = 'student', userData = null }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedView, setExpandedView] = useState(false);

    const studentStatus = String(userData?.status || userData?.rank || '').toLowerCase();
    const isAlumni = userType === 'student' && studentStatus === 'alumni';

    useEffect(() => {
        fetchAvailableReports();
    }, [userType]);

    const fetchAvailableReports = async () => {
        try {
            setLoading(true);
            // Enhanced reports based on user type with better categorization
            const availableReports = [
                {
                    id: 'schedule_history',
                    title: 'Complete Schedule History',
                    description: 'Comprehensive view of your rotation schedule with performance metrics',
                    icon: '📅',
                    category: 'Academic',
                    allowedFor: ['student', 'preceptor'],
                    estimatedTime: '1-2 minutes',
                    priority: 'high'
                },
                {
                    id: 'performance_analytics',
                    title: 'Performance Analytics Dashboard',
                    description: 'Detailed analysis of your academic performance and progress trends',
                    icon: '📊',
                    category: 'Performance',
                    allowedFor: ['student'],
                    estimatedTime: '2-3 minutes',
                    priority: 'high'
                },
                {
                    id: 'contact_export',
                    title: 'Personal Information Export',
                    description: 'Complete export of your profile, contact details, and academic status',
                    icon: '📋',
                    category: 'Personal',
                    allowedFor: ['student', 'preceptor'],
                    estimatedTime: '30 seconds',
                    priority: 'medium'
                },
                {
                    id: 'evaluation_summary',
                    title: 'Evaluation Summary Report',
                    description: 'Comprehensive summary of all evaluations received and feedback',
                    icon: '⭐',
                    category: 'Academic',
                    allowedFor: ['student'],
                    estimatedTime: '1-2 minutes',
                    priority: 'high'
                },
                {
                    id: 'teaching_portfolio',
                    title: 'Teaching Portfolio Report',
                    description: 'Summary of your teaching activities and student evaluations',
                    icon: '🎓',
                    category: 'Teaching',
                    allowedFor: ['preceptor'],
                    estimatedTime: '2-4 minutes',
                    priority: 'high'
                },
                {
                    id: 'attendance_record',
                    title: 'Attendance & Compliance Record',
                    description: 'Complete attendance history with compliance metrics',
                    icon: '✅',
                    category: 'Compliance',
                    allowedFor: ['student', 'preceptor'],
                    estimatedTime: '1 minute',
                    priority: 'medium'
                },
                {
                    id: 'mentorship_report',
                    title: 'Mentorship Impact Report',
                    description: 'Analysis of your mentorship activities and student outcomes',
                    icon: '🤝',
                    category: 'Professional',
                    allowedFor: ['preceptor'],
                    estimatedTime: '3-5 minutes',
                    priority: 'medium'
                },
                {
                    id: 'certification_record',
                    title: 'Certifications & Achievements',
                    description: 'Official record of all certifications, awards, and achievements',
                    icon: '🏆',
                    category: 'Academic',
                    allowedFor: ['student', 'preceptor'],
                    estimatedTime: '1-2 minutes',
                    priority: 'low'
                }
            ];

            // Filter reports based on user type
            const filteredReports = availableReports.filter(report => 
                report.allowedFor.includes(userType)
            );

            setReports(filteredReports);
        } catch (err) {
            setError('Failed to load available reports');
        } finally {
            setLoading(false);
        }
    };

    const generateReport = async (reportId) => {
        try {
            setLoadingReport(true);
            setSelectedReport(reportId);
            
            // Simulate API call with enhanced data structure
            const endpoint = `/reports/${userType}/${reportId}`;
            
            // Create more comprehensive mock data
            let mockData = {};
            
            switch (reportId) {
                case 'schedule_history':
                    mockData = {
                        title: 'Complete Schedule History Report',
                        generated: new Date().toISOString(),
                        reportType: 'Academic Record',
                        period: isAlumni ? 'Complete Program' : 'Current Progress',
                        summary: {
                            totalRotations: isAlumni ? 8 : 6,
                            completedRotations: isAlumni ? 8 : 4,
                            currentStatus: isAlumni ? 'Program Completed' : 'Active Student',
                            overallGrade: isAlumni ? 'A' : 'B+',
                            attendanceRate: isAlumni ? '98%' : '95%'
                        },
                        data: {
                            rotations: [
                                { 
                                    number: 1, 
                                    specialty: 'Internal Medicine', 
                                    institution: 'Qatar University Hospital', 
                                    startDate: '2023-09-01',
                                    endDate: '2023-10-31',
                                    preceptor: 'Dr. Ahmed Al-Rashid',
                                    grade: 'A',
                                    attendance: '100%',
                                    completed: true 
                                },
                                { 
                                    number: 2, 
                                    specialty: 'Pediatrics', 
                                    institution: 'Sidra Medicine', 
                                    startDate: '2023-11-01',
                                    endDate: '2023-12-31',
                                    preceptor: 'Dr. Fatima Al-Zahra',
                                    grade: 'A-',
                                    attendance: '98%',
                                    completed: true 
                                },
                                { 
                                    number: 3, 
                                    specialty: 'Cardiology', 
                                    institution: 'Heart Hospital', 
                                    startDate: '2024-01-01',
                                    endDate: '2024-02-29',
                                    preceptor: 'Dr. Mohammed Khalil',
                                    grade: 'B+',
                                    attendance: '95%',
                                    completed: true 
                                },
                                { 
                                    number: 4, 
                                    specialty: 'Psychiatry', 
                                    institution: 'Mental Health Center', 
                                    startDate: '2024-03-01',
                                    endDate: '2024-04-30',
                                    preceptor: 'Dr. Sarah Ibrahim',
                                    grade: 'A',
                                    attendance: '97%',
                                    completed: true 
                                },
                                ...(isAlumni ? [
                                    { 
                                        number: 5, 
                                        specialty: 'Emergency Medicine', 
                                        institution: 'Hamad Medical Corporation', 
                                        startDate: '2024-05-01',
                                        endDate: '2024-06-30',
                                        preceptor: 'Dr. Omar Hassan',
                                        grade: 'A',
                                        attendance: '99%',
                                        completed: true 
                                    },
                                    { 
                                        number: 6, 
                                        specialty: 'Community Pharmacy', 
                                        institution: 'Al-Nahda Pharmacy', 
                                        startDate: '2024-07-01',
                                        endDate: '2024-08-31',
                                        preceptor: 'PharmD. Amina Al-Kuwari',
                                        grade: 'A+',
                                        attendance: '100%',
                                        completed: true 
                                    },
                                    { 
                                        number: 7, 
                                        specialty: 'Clinical Research', 
                                        institution: 'Qatar Biomedical Research Institute', 
                                        startDate: '2024-09-01',
                                        endDate: '2024-10-31',
                                        preceptor: 'Dr. Khalid Al-Mannai',
                                        grade: 'A',
                                        attendance: '98%',
                                        completed: true 
                                    },
                                    { 
                                        number: 8, 
                                        specialty: 'Ambulatory Care', 
                                        institution: 'Primary Healthcare Corporation', 
                                        startDate: '2024-11-01',
                                        endDate: '2024-12-31',
                                        preceptor: 'PharmD. Layla Al-Thani',
                                        grade: 'A+',
                                        attendance: '100%',
                                        completed: true 
                                    }
                                ] : [])
                            ],
                            achievements: isAlumni ? [
                                'Dean\'s List - Fall 2023',
                                'Outstanding Clinical Performance Award',
                                'Research Excellence Certificate',
                                'Community Service Recognition'
                            ] : [
                                'Dean\'s List - Fall 2023',
                                'Clinical Excellence Award'
                            ]
                        }
                    };
                    break;
                    
                case 'performance_analytics':
                    mockData = {
                        title: 'Performance Analytics Dashboard',
                        generated: new Date().toISOString(),
                        reportType: 'Performance Analysis',
                        period: isAlumni ? 'Complete Program Analysis' : 'Current Performance Tracking',
                        summary: {
                            overallGPA: isAlumni ? '3.85' : '3.72',
                            clinicalGrade: isAlumni ? 'A' : 'B+',
                            attendanceRate: isAlumni ? '98.5%' : '95.2%',
                            improvementTrend: 'Increasing',
                            classRank: isAlumni ? '5th/120' : '12th/98'
                        },
                        data: {
                            skillsAssessment: {
                                clinicalKnowledge: isAlumni ? 92 : 87,
                                patientCommunication: isAlumni ? 95 : 89,
                                professionalismScore: isAlumni ? 98 : 92,
                                teamworkRating: isAlumni ? 94 : 88,
                                leadershipPotential: isAlumni ? 90 : 85
                            },
                            monthlyProgress: [
                                { month: 'Sep 2023', score: 82 },
                                { month: 'Oct 2023', score: 85 },
                                { month: 'Nov 2023', score: 87 },
                                { month: 'Dec 2023', score: 89 },
                                { month: 'Jan 2024', score: 88 },
                                { month: 'Feb 2024', score: 91 },
                                { month: 'Mar 2024', score: 93 },
                                { month: 'Apr 2024', score: 94 }
                            ],
                            strengths: [
                                'Excellent patient interaction skills',
                                'Strong clinical reasoning abilities',
                                'Consistent academic performance',
                                'Leadership qualities'
                            ],
                            areasForImprovement: [
                                'Time management during busy rotations',
                                'Advanced pharmacotherapy knowledge',
                                'Research methodology skills'
                            ]
                        }
                    };
                    break;
                    
                case 'contact_export':
                    mockData = {
                        title: 'Personal Information Export',
                        generated: new Date().toISOString(),
                        reportType: 'Personal Data Export',
                        summary: {
                            profileCompleteness: '98%',
                            lastUpdated: '2024-11-15',
                            dataCategories: 6
                        },
                        data: {
                            personalInfo: {
                                fullName: `${userData?.firstname} ${userData?.lastname}`,
                                studentId: userData?._id,
                                email: userData?.email,
                                phone: userData?.phone,
                                address: userData?.address,
                                emergencyContact: 'Parent/Guardian Name',
                                emergencyPhone: '+974-XXXX-XXXX',
                                nationality: 'Qatari',
                                dateOfBirth: '1999-05-15'
                            },
                            academicInfo: {
                                program: 'Doctor of Pharmacy (PharmD)',
                                currentRank: userData?.rank,
                                enrollmentDate: '2021-09-01',
                                expectedGraduation: isAlumni ? '2024-12-31' : '2025-06-30',
                                currentGPA: isAlumni ? '3.85' : '3.72',
                                totalCredits: isAlumni ? 140 : 108
                            },
                            institutionalAffiliations: [
                                'Qatar University - College of Pharmacy',
                                'Qatar University Hospital',
                                'Sidra Medicine',
                                'Hamad Medical Corporation'
                            ]
                        }
                    };
                    break;
                    
                case 'teaching_portfolio':
                    mockData = {
                        title: 'Teaching Portfolio Report',
                        generated: new Date().toISOString(),
                        reportType: 'Teaching Performance Analysis',
                        summary: {
                            totalStudentsSupervised: 47,
                            averageStudentRating: 4.7,
                            teachingExperienceYears: 8,
                            specializations: 3
                        },
                        data: {
                            supervisionHistory: [
                                { year: '2023-2024', students: 15, avgRating: 4.8, specialty: 'Internal Medicine' },
                                { year: '2022-2023', students: 18, avgRating: 4.6, specialty: 'Cardiology' },
                                { year: '2021-2022', students: 14, avgRating: 4.7, specialty: 'Emergency Medicine' }
                            ],
                            feedbackHighlights: [
                                'Excellent mentor and teacher',
                                'Provides constructive feedback',
                                'Creates supportive learning environment',
                                'Expert clinical knowledge'
                            ],
                            professionalDevelopment: [
                                'Advanced Teaching Methods Certificate',
                                'Clinical Supervision Training',
                                'Medical Education Leadership Program'
                            ]
                        }
                    };
                    break;
                    
                default:
                    mockData = {
                        title: `${reportId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Report`,
                        generated: new Date().toISOString(),
                        reportType: 'Standard Report',
                        summary: {
                            status: 'Available',
                            dataPoints: Math.floor(Math.random() * 100) + 50
                        },
                        data: { 
                            message: 'Comprehensive report data will be available soon',
                            estimatedCompletion: 'Next system update'
                        }
                    };
            }
            
            setReportData(mockData);
        } catch (err) {
            setError('Failed to generate report');
        } finally {
            setLoadingReport(false);
        }
    };

    const exportReport = (format) => {
        if (!reportData) return;
        
        const filename = `${reportData.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;
        
        let content, mimeType, extension;
        
        switch (format) {
            case 'json':
                content = JSON.stringify(reportData, null, 2);
                mimeType = 'application/json';
                extension = 'json';
                break;
            case 'csv':
                content = convertToCSV(reportData);
                mimeType = 'text/csv';
                extension = 'csv';
                break;
            case 'pdf':
                content = generatePDFContent(reportData);
                mimeType = 'application/pdf';
                extension = 'pdf';
                break;
            default:
                return;
        }
        
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.${extension}`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const convertToCSV = (data) => {
        let csv = `${data.title}\nGenerated: ${new Date(data.generated).toLocaleString()}\nReport Type: ${data.reportType || 'Standard'}\n\n`;
        
        if (data.summary) {
            csv += 'Summary\n';
            Object.entries(data.summary).forEach(([key, value]) => {
                csv += `${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())},${value}\n`;
            });
            csv += '\n';
        }
        
        if (data.data.rotations) {
            csv += 'Rotation History\n';
            csv += 'Number,Specialty,Institution,Start Date,End Date,Preceptor,Grade,Attendance,Status\n';
            data.data.rotations.forEach(rotation => {
                csv += `${rotation.number},${rotation.specialty},${rotation.institution},${rotation.startDate || 'N/A'},${rotation.endDate || 'N/A'},${rotation.preceptor || 'N/A'},${rotation.grade || 'N/A'},${rotation.attendance || 'N/A'},${rotation.completed ? 'Completed' : 'In Progress'}\n`;
            });
            csv += '\n';
        }
        
        return csv;
    };

    const generatePDFContent = (data) => {
        return `PDF Report: ${data.title}\nGenerated: ${new Date(data.generated).toLocaleString()}\n\nThis would be a properly formatted PDF document with all report data.`;
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Academic': '#3b82f6',
            'Performance': '#10b981',
            'Personal': '#8b5cf6',
            'Teaching': '#f59e0b',
            'Compliance': '#ef4444',
            'Professional': '#06b6d4'
        };
        return colors[category] || '#6b7280';
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'high': return <FaExclamationTriangle style={{ color: '#ef4444' }} />;
            case 'medium': return <FaInfoCircle style={{ color: '#f59e0b' }} />;
            case 'low': return <FaCheckCircle style={{ color: '#10b981' }} />;
            default: return <FaInfoCircle style={{ color: '#6b7280' }} />;
        }
    };

    // Filter reports based on search term
    const filteredReports = reports.filter(report =>
        report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
                <LoadingSpinner text="Loading your report center..." variant="page" />
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '2rem', 
            minHeight: '100vh', 
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '20px',
                padding: '2.5rem',
                marginBottom: '2rem',
                color: 'white',
                boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.2)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background decorations */}
                <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    filter: 'blur(40px)'
                }}></div>
                <div style={{
                    position: 'absolute',
                    bottom: '-30px',
                    left: '-30px',
                    width: '150px',
                    height: '150px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '50%',
                    filter: 'blur(30px)'
                }}></div>
                
                <div style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '1rem'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '16px',
                            padding: '1rem',
                            backdropFilter: 'blur(10px)'
                        }}>
                            {userType === 'student' ? <FaGraduationCap style={{ fontSize: '2rem' }} /> : <FaUser style={{ fontSize: '2rem' }} />}
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '2.5rem', fontWeight: 700 }}>
                                {isAlumni ? 'Alumni' : userType === 'student' ? 'Student' : 'Preceptor'} Report Center
                            </h1>
                            <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '1.1rem' }}>
                                {isAlumni 
                                    ? 'Access your complete academic journey and achievements' 
                                    : userType === 'student' 
                                        ? 'Track your academic progress and performance'
                                        : 'Monitor your teaching impact and student outcomes'
                                }
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={fetchAvailableReports}
                        style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '12px',
                            padding: '0.75rem',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                        }}
                    >
                        <FaSync />
                    </button>
                </div>
            </div>

            {error && (
                <div style={{
                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                    border: '1px solid #f87171',
                    color: '#991b1b',
                    padding: '1rem 1.5rem',
                    borderRadius: '12px',
                    marginBottom: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.1)'
                }}>
                    <FaExclamationTriangle style={{ fontSize: '1.25rem', flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>{error}</span>
                </div>
            )}

            {isAlumni && (
                <div style={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                    border: '2px solid #10b981',
                    borderRadius: '16px',
                    padding: '2rem',
                    marginBottom: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5rem',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.1)'
                }}>
                    <div style={{
                        fontSize: '3rem',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        borderRadius: '50%',
                        padding: '1rem',
                        color: 'white',
                        flexShrink: 0
                    }}>
                        🎓
                    </div>
                    <div>
                        <h3 style={{ 
                            margin: '0 0 0.5rem 0', 
                            color: '#065f46', 
                            fontSize: '1.5rem', 
                            fontWeight: 700 
                        }}>
                            Alumni Access Privileges
                        </h3>
                        <p style={{ 
                            margin: 0, 
                            color: '#047857', 
                            fontSize: '1rem',
                            lineHeight: 1.6
                        }}>
                            As a program graduate, you have lifetime access to generate and export all your academic reports, 
                            transcripts, and achievement records. Your data remains available for career and licensing purposes.
                        </p>
                    </div>
                </div>
            )}

            {/* Search and Stats */}
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '1.5rem',
                marginBottom: '2rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
            }}>
                <div style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '1rem'
                }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: '300px' }}>
                        <FaSearch style={{
                            position: 'absolute',
                            left: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            fontSize: '0.875rem'
                        }} />
                        <input
                            type="text"
                            placeholder="Search reports by title, category, or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                    </div>
                </div>

                {/* Quick Stats */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>
                            {filteredReports.length}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Available Reports
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
                            {new Set(reports.map(r => r.category)).size}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Categories
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>
                            {reports.filter(r => r.priority === 'high').length}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Priority Reports
                        </div>
                    </div>
                </div>
            </div>

            {/* Reports Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {filteredReports.map(report => (
                    <div key={report.id} style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                        overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                    }}>
                        {/* Header */}
                        <div style={{
                            background: `linear-gradient(135deg, ${getCategoryColor(report.category)}dd 0%, ${getCategoryColor(report.category)} 100%)`,
                            color: 'white',
                            padding: '1.5rem',
                            position: 'relative'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{
                                    fontSize: '2rem',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    borderRadius: '12px',
                                    padding: '0.75rem',
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    {report.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <h3 style={{ 
                                            margin: 0, 
                                            fontSize: '1.2rem', 
                                            fontWeight: 700,
                                            lineHeight: 1.3,
                                            flex: 1
                                        }}>
                                            {report.title}
                                        </h3>
                                        {getPriorityIcon(report.priority)}
                                    </div>
                                    <div style={{
                                        background: 'rgba(255, 255, 255, 0.3)',
                                        color: 'white',
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        display: 'inline-block'
                                    }}>
                                        {report.category}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '1.5rem' }}>
                            <p style={{ 
                                margin: '0 0 1rem 0', 
                                color: '#6b7280', 
                                fontSize: '0.875rem',
                                lineHeight: 1.5
                            }}>
                                {report.description}
                            </p>

                            {/* Metadata */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1.5rem',
                                padding: '1rem',
                                background: '#f8fafc',
                                borderRadius: '8px',
                                fontSize: '0.75rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FaCalendarAlt style={{ color: '#6b7280' }} />
                                    <span style={{ color: '#374151' }}>
                                        {report.estimatedTime}
                                    </span>
                                </div>
                                <div style={{
                                    background: report.priority === 'high' 
                                        ? '#fee2e2' 
                                        : report.priority === 'medium' 
                                            ? '#fef3c7' 
                                            : '#ecfdf5',
                                    color: report.priority === 'high' 
                                        ? '#991b1b' 
                                        : report.priority === 'medium' 
                                            ? '#92400e' 
                                            : '#065f46',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '12px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase'
                                }}>
                                    {report.priority} Priority
                                </div>
                            </div>

                            {/* Action Button */}
                            <button 
                                onClick={() => generateReport(report.id)}
                                disabled={loadingReport && selectedReport === report.id}
                                style={{
                                    width: '100%',
                                    background: loadingReport && selectedReport === report.id
                                        ? '#9ca3af' 
                                        : `linear-gradient(135deg, ${getCategoryColor(report.category)} 0%, ${getCategoryColor(report.category)}dd 100%)`,
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.875rem',
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    cursor: loadingReport && selectedReport === report.id ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.2s ease',
                                    boxShadow: loadingReport && selectedReport === report.id 
                                        ? 'none' 
                                        : `0 4px 12px ${getCategoryColor(report.category)}33`
                                }}
                                onMouseEnter={(e) => {
                                    if (!loadingReport || selectedReport !== report.id) {
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = `0 6px 16px ${getCategoryColor(report.category)}44`;
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loadingReport || selectedReport !== report.id) {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = `0 4px 12px ${getCategoryColor(report.category)}33`;
                                    }
                                }}
                            >
                                {loadingReport && selectedReport === report.id ? (
                                    <>
                                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                                        Generating Report...
                                    </>
                                ) : (
                                    <>
                                        <FaFileAlt />
                                        Generate Report
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* No Results */}
            {filteredReports.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                    <FaFileAlt style={{ fontSize: '4rem', color: '#9ca3af', marginBottom: '1rem' }} />
                    <h2 style={{ 
                        margin: '0 0 0.5rem 0', 
                        color: '#1f2937', 
                        fontSize: '1.5rem', 
                        fontWeight: 700 
                    }}>
                        No Reports Found
                    </h2>
                    <p style={{ margin: '0', color: '#6b7280', fontSize: '1rem' }}>
                        {searchTerm ? 'No reports match your search criteria.' : 'No reports are currently available.'}
                    </p>
                </div>
            )}

            {/* Report Viewer */}
            {reportData && (
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    marginTop: '2rem',
                    border: '1px solid #e5e7eb'
                }}>
                    {/* Report Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        color: 'white',
                        padding: '2rem',
                        position: 'relative'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            gap: '1rem'
                        }}>
                            <div style={{ flex: 1 }}>
                                <h3 style={{ 
                                    margin: '0 0 0.5rem 0', 
                                    fontSize: '1.8rem', 
                                    fontWeight: 700 
                                }}>
                                    {reportData.title}
                                </h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                                    <p style={{ 
                                        margin: 0, 
                                        opacity: 0.9, 
                                        fontSize: '0.875rem' 
                                    }}>
                                        Generated: {new Date(reportData.generated).toLocaleString()}
                                    </p>
                                    {reportData.reportType && (
                                        <span style={{
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '12px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600
                                        }}>
                                            {reportData.reportType}
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <button 
                                    onClick={() => exportReport('csv')}
                                    style={{
                                        background: 'rgba(16, 185, 129, 0.2)',
                                        border: '2px solid #10b981',
                                        borderRadius: '8px',
                                        padding: '0.5rem 1rem',
                                        color: '#10b981',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#10b981';
                                        e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(16, 185, 129, 0.2)';
                                        e.target.style.color = '#10b981';
                                    }}
                                >
                                    <FaFileExcel />
                                    CSV
                                </button>
                                
                                <button 
                                    onClick={() => exportReport('json')}
                                    style={{
                                        background: 'rgba(245, 158, 11, 0.2)',
                                        border: '2px solid #f59e0b',
                                        borderRadius: '8px',
                                        padding: '0.5rem 1rem',
                                        color: '#f59e0b',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#f59e0b';
                                        e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(245, 158, 11, 0.2)';
                                        e.target.style.color = '#f59e0b';
                                    }}
                                >
                                    <FaFileAlt />
                                    JSON
                                </button>
                                
                                <button 
                                    onClick={() => exportReport('pdf')}
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.2)',
                                        border: '2px solid #ef4444',
                                        borderRadius: '8px',
                                        padding: '0.5rem 1rem',
                                        color: '#ef4444',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#ef4444';
                                        e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                        e.target.style.color = '#ef4444';
                                    }}
                                >
                                    <FaFilePdf />
                                    PDF
                                </button>
                                
                                <button 
                                    onClick={() => setExpandedView(!expandedView)}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        border: '2px solid white',
                                        borderRadius: '8px',
                                        padding: '0.5rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {expandedView ? <FaCompress /> : <FaExpand />}
                                </button>
                                
                                <button 
                                    onClick={() => setReportData(null)}
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.2)',
                                        border: '2px solid #ef4444',
                                        borderRadius: '8px',
                                        padding: '0.5rem',
                                        color: '#ef4444',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#ef4444';
                                        e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                        e.target.style.color = '#ef4444';
                                    }}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Report Content */}
                    <div style={{ padding: '2rem' }}>
                        {/* Summary Cards */}
                        {reportData.summary && (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '1rem',
                                marginBottom: '2rem'
                            }}>
                                {Object.entries(reportData.summary).map(([key, value], index) => {
                                    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                                    const color = colors[index % colors.length];
                                    
                                    return (
                                        <div key={key} style={{
                                            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                                            color: 'white',
                                            padding: '1.5rem',
                                            borderRadius: '12px',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                                                {value}
                                            </div>
                                            <div style={{ 
                                                opacity: 0.9, 
                                                fontSize: '0.875rem',
                                                textTransform: 'capitalize'
                                            }}>
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Data Tables */}
                        {reportData.data.rotations && (
                            <div style={{
                                background: '#f8fafc',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                marginBottom: '2rem'
                            }}>
                                <h4 style={{ 
                                    margin: '0 0 1.5rem 0', 
                                    fontSize: '1.25rem', 
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <FaCalendarAlt style={{ color: '#3b82f6' }} />
                                    Rotation History
                                </h4>
                                
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse',
                                        background: 'white',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <thead>
                                            <tr style={{ background: '#f3f4f6' }}>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>#</th>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Specialty</th>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Institution</th>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Preceptor</th>
                                                <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Grade</th>
                                                <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Attendance</th>
                                                <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.data.rotations.map((rotation, index) => (
                                                <tr key={index} style={{ 
                                                    borderBottom: '1px solid #e5e7eb',
                                                    transition: 'background-color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                >
                                                    <td style={{ padding: '0.75rem', color: '#374151', fontWeight: 600 }}>
                                                        {rotation.number}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', color: '#374151' }}>
                                                        {rotation.specialty}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.875rem' }}>
                                                        {rotation.institution}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.875rem' }}>
                                                        {rotation.preceptor || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                        {rotation.grade && (
                                                            <span style={{
                                                                background: rotation.grade.startsWith('A') 
                                                                    ? '#dcfce7' 
                                                                    : rotation.grade.startsWith('B') 
                                                                        ? '#fef3c7' 
                                                                        : '#fee2e2',
                                                                color: rotation.grade.startsWith('A') 
                                                                    ? '#166534' 
                                                                    : rotation.grade.startsWith('B') 
                                                                        ? '#92400e' 
                                                                        : '#991b1b',
                                                                padding: '0.25rem 0.75rem',
                                                                borderRadius: '12px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600
                                                            }}>
                                                                {rotation.grade}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                                                        {rotation.attendance || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                        <span style={{
                                                            background: rotation.completed ? '#dcfce7' : '#fef3c7',
                                                            color: rotation.completed ? '#166534' : '#92400e',
                                                            padding: '0.25rem 0.75rem',
                                                            borderRadius: '12px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600
                                                        }}>
                                                            {rotation.completed ? 'Completed' : 'In Progress'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Skills Assessment */}
                        {reportData.data.skillsAssessment && (
                            <div style={{
                                background: '#f8fafc',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                marginBottom: '2rem'
                            }}>
                                <h4 style={{ 
                                    margin: '0 0 1.5rem 0', 
                                    fontSize: '1.25rem', 
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <FaStar style={{ color: '#f59e0b' }} />
                                    Skills Assessment
                                </h4>
                                
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: '1rem'
                                }}>
                                    {Object.entries(reportData.data.skillsAssessment).map(([skill, score]) => (
                                        <div key={skill} style={{
                                            background: 'white',
                                            borderRadius: '8px',
                                            padding: '1rem',
                                            border: '1px solid #e5e7eb'
                                        }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                marginBottom: '0.5rem'
                                            }}>
                                                <span style={{ 
                                                    fontSize: '0.875rem', 
                                                    fontWeight: 600, 
                                                    color: '#374151',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {skill.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                </span>
                                                <span style={{ 
                                                    fontSize: '1rem', 
                                                    fontWeight: 700, 
                                                    color: score >= 90 ? '#10b981' : score >= 80 ? '#f59e0b' : '#ef4444'
                                                }}>
                                                    {score}%
                                                </span>
                                            </div>
                                            <div style={{
                                                width: '100%',
                                                height: '8px',
                                                background: '#e5e7eb',
                                                borderRadius: '4px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${score}%`,
                                                    height: '100%',
                                                    background: score >= 90 
                                                        ? 'linear-gradient(90deg, #10b981, #059669)' 
                                                        : score >= 80 
                                                            ? 'linear-gradient(90deg, #f59e0b, #d97706)' 
                                                            : 'linear-gradient(90deg, #ef4444, #dc2626)',
                                                    borderRadius: '4px',
                                                    transition: 'width 0.5s ease'
                                                }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Achievements */}
                        {reportData.data.achievements && reportData.data.achievements.length > 0 && (
                            <div style={{
                                background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                                border: '1px solid #10b981',
                                borderRadius: '12px',
                                padding: '1.5rem'
                            }}>
                                <h4 style={{ 
                                    margin: '0 0 1rem 0', 
                                    fontSize: '1.25rem', 
                                    fontWeight: 600,
                                    color: '#065f46',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <FaCertificate style={{ color: '#10b981' }} />
                                    Achievements & Recognition
                                </h4>
                                
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: '0.75rem'
                                }}>
                                    {reportData.data.achievements.map((achievement, index) => (
                                        <div key={index} style={{
                                            background: 'white',
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: '1px solid #a7f3d0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem'
                                        }}>
                                            <FaCheckCircle style={{ color: '#10b981', flexShrink: 0 }} />
                                            <span style={{ 
                                                color: '#047857', 
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            }}>
                                                {achievement}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NonAdminReportCenter;
